import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowLeft, Trophy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Tournament } from "@shared/schema";

const tournamentFormSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  description: z.string().optional(),
  category: z.enum(["open", "beginner", "intermediate", "advanced", "pro", "mixed", "women", "men"]),
  format: z.enum(["single_elimination", "double_elimination", "round_robin", "groups_then_elimination"]),
  maxTeams: z.coerce.number().min(4, "Mínimo 4 equipos").max(128, "Máximo 128 equipos"),
  minTeams: z.coerce.number().min(2, "Mínimo 2 equipos"),
  startDate: z.string().min(1, "Fecha de inicio requerida"),
  endDate: z.string().optional(),
  registrationStartDate: z.string().optional(),
  registrationEndDate: z.string().optional(),
  entryFee: z.coerce.number().min(0, "La cuota no puede ser negativa").default(0),
  prizePool: z.string().optional(),
  courtType: z.enum(["indoor", "outdoor", "both"]),
  setsToWin: z.coerce.number().min(1).max(3).default(2),
  gamesPerSet: z.coerce.number().min(4).max(8).default(6),
  rules: z.string().optional(),
});

type TournamentFormValues = z.infer<typeof tournamentFormSchema>;

interface TournamentFormProps {
  tournamentId?: string;
  onBack: () => void;
  onSuccess: () => void;
}

export function TournamentForm({ tournamentId, onBack, onSuccess }: TournamentFormProps) {
  const { toast } = useToast();
  const isEditing = !!tournamentId;

  const { data: existingTournament, isLoading: isLoadingTournament } = useQuery<{ tournament: Tournament }>({
    queryKey: ["/api/club/tournaments", tournamentId],
    enabled: isEditing,
  });

  const form = useForm<TournamentFormValues>({
    resolver: zodResolver(tournamentFormSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "open",
      format: "single_elimination",
      maxTeams: 16,
      minTeams: 4,
      startDate: "",
      endDate: "",
      registrationStartDate: "",
      registrationEndDate: "",
      entryFee: 0,
      prizePool: "",
      courtType: "both",
      setsToWin: 2,
      gamesPerSet: 6,
      rules: "",
    },
  });

  useEffect(() => {
    if (existingTournament?.tournament) {
      const t = existingTournament.tournament;
      form.reset({
        name: t.name,
        description: t.description || "",
        category: t.category as any || "open",
        format: t.format as any || "single_elimination",
        maxTeams: t.maxTeams || 16,
        minTeams: t.minTeams || 4,
        startDate: t.startDate ? new Date(t.startDate).toISOString().split("T")[0] : "",
        endDate: t.endDate ? new Date(t.endDate).toISOString().split("T")[0] : "",
        registrationStartDate: t.registrationStartDate ? new Date(t.registrationStartDate).toISOString().split("T")[0] : "",
        registrationEndDate: t.registrationEndDate ? new Date(t.registrationEndDate).toISOString().split("T")[0] : "",
        entryFee: t.entryFee || 0,
        prizePool: t.prizePool || "",
        courtType: t.courtType as any || "both",
        setsToWin: t.setsToWin || 2,
        gamesPerSet: t.gamesPerSet || 6,
        rules: t.rules || "",
      });
    }
  }, [existingTournament, form]);

  const createMutation = useMutation({
    mutationFn: async (data: TournamentFormValues) => {
      if (isEditing) {
        return apiRequest("PATCH", `/api/club/tournaments/${tournamentId}`, data);
      }
      return apiRequest("POST", "/api/club/tournaments", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments"] });
      toast({
        title: isEditing ? "Torneo actualizado" : "Torneo creado",
        description: isEditing 
          ? "Los cambios han sido guardados correctamente."
          : "El torneo ha sido creado correctamente.",
      });
      onSuccess();
    },
    onError: () => {
      toast({
        title: "Error",
        description: `No se pudo ${isEditing ? "actualizar" : "crear"} el torneo.`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TournamentFormValues) => {
    createMutation.mutate(data);
  };

  if (isEditing && isLoadingTournament) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            {isEditing ? "Editar Torneo" : "Crear Torneo"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {isEditing ? "Modifica los detalles del torneo" : "Configura los detalles de tu nuevo torneo"}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <p className="text-xs text-muted-foreground">
            Los campos marcados con <span className="text-destructive">*</span> son obligatorios.
          </p>
          <Card>
            <CardHeader>
              <CardTitle>Información Básica</CardTitle>
              <CardDescription>Datos principales del torneo</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Nombre del Torneo <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Ej: Torneo de Verano 2025" 
                        {...field} 
                        data-testid="input-tournament-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descripción (opcional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe el torneo, premios, reglas especiales..."
                        className="resize-none"
                        {...field}
                        data-testid="input-tournament-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Categoría <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-tournament-category">
                            <SelectValue placeholder="Selecciona categoría" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="open">Abierta</SelectItem>
                          <SelectItem value="beginner">Principiante</SelectItem>
                          <SelectItem value="intermediate">Intermedio</SelectItem>
                          <SelectItem value="advanced">Avanzado</SelectItem>
                          <SelectItem value="pro">Profesional</SelectItem>
                          <SelectItem value="mixed">Mixto</SelectItem>
                          <SelectItem value="women">Femenino</SelectItem>
                          <SelectItem value="men">Masculino</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="format"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Formato <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-tournament-format">
                            <SelectValue placeholder="Selecciona formato" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="single_elimination">Eliminatoria Simple</SelectItem>
                          <SelectItem value="double_elimination">Eliminatoria Doble</SelectItem>
                          <SelectItem value="round_robin">Todos contra Todos</SelectItem>
                          <SelectItem value="groups_then_elimination">Grupos + Eliminatoria</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Equipos y Fechas</CardTitle>
              <CardDescription>Configuración de participantes y calendario</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="minTeams"
                  render={({ field }) => (
                    <FormItem>
                        <FormLabel>
                          Mínimo de Equipos <span className="text-destructive">*</span>
                        </FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={2}
                          {...field} 
                          data-testid="input-min-teams"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxTeams"
                  render={({ field }) => (
                    <FormItem>
                        <FormLabel>
                          Máximo de Equipos <span className="text-destructive">*</span>
                        </FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={4}
                          max={128}
                          {...field} 
                          data-testid="input-max-teams"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Fecha de Inicio <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                          data-testid="input-start-date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Fin (opcional)</FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                          data-testid="input-end-date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="registrationStartDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Apertura de Inscripciones (opcional)</FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                          data-testid="input-registration-start"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="registrationEndDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cierre de Inscripciones (opcional)</FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                          data-testid="input-registration-end"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configuración de Partidos</CardTitle>
              <CardDescription>Reglas y formato de los partidos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="courtType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Pista (opcional)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-court-type">
                            <SelectValue placeholder="Selecciona tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="indoor">Interior</SelectItem>
                          <SelectItem value="outdoor">Exterior</SelectItem>
                          <SelectItem value="both">Ambas</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="setsToWin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sets para Ganar (opcional)</FormLabel>
                      <Select onValueChange={(v) => field.onChange(parseInt(v))} value={String(field.value)}>
                        <FormControl>
                          <SelectTrigger data-testid="select-sets-to-win">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1">1 (Al mejor de 1)</SelectItem>
                          <SelectItem value="2">2 (Al mejor de 3)</SelectItem>
                          <SelectItem value="3">3 (Al mejor de 5)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gamesPerSet"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Juegos por Set (opcional)</FormLabel>
                      <Select onValueChange={(v) => field.onChange(parseInt(v))} value={String(field.value)}>
                        <FormControl>
                          <SelectTrigger data-testid="select-games-per-set">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="4">4 juegos</SelectItem>
                          <SelectItem value="6">6 juegos</SelectItem>
                          <SelectItem value="8">8 juegos</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="entryFee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cuota de Inscripción ($) (opcional)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={0}
                          step={0.01}
                          {...field} 
                          data-testid="input-entry-fee"
                        />
                      </FormControl>
                      <FormDescription>0 para torneo gratuito</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prizePool"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Premios (opcional)</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Ej: 1º: $500, 2º: $250, 3º: $100"
                          {...field} 
                          data-testid="input-prize-pool"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="rules"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reglas Adicionales (opcional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Reglas específicas del torneo..."
                        className="resize-none min-h-[100px]"
                        {...field}
                        data-testid="input-tournament-rules"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={onBack} data-testid="button-cancel">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={createMutation.isPending}
              data-testid="button-save-tournament"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? "Guardar Cambios" : "Crear Torneo"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
